import useWorkspacePermissions from '@hooks/workspacePermissions';
import useWorkspace from '@hooks/useWorkspace';
import Spinner from '@components/spinner/Spinner';
import { Box, Button, Typography } from '@mui/material';
import { ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WORKSPACE_CAPABILITY } from '@constants/workspacePermissions';

/**
 * Route/page guard that requires a workspace capability flag.
 * Shows a no-access state when the active workspace user lacks permission.
 */
const RequireWorkspacePermission = ({
  children,
  capability = WORKSPACE_CAPABILITY.CAN_EDIT,
  fallbackPath = '/dashboard',
}) => {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspace();
  const permissions = useWorkspacePermissions(activeWorkspaceId);

  if (permissions.isLoading || !activeWorkspaceId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Spinner />
      </Box>
    );
  }

  if (!permissions[capability]) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          p: 3,
          textAlign: 'center',
        }}
      >
        <ShieldAlert size={56} color="#d32f2f" style={{ marginBottom: 20 }} />
        <Typography variant="h5" gutterBottom>
          No Access
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
          You don&apos;t have permission to perform this action in the current workspace. Contact a
          workspace admin if you need access.
        </Typography>
        <Button variant="contained" onClick={() => navigate(fallbackPath, { replace: true })}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return children;
};

export default RequireWorkspacePermission;
